import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Phone, MapPin, VideoCamera } from "@phosphor-icons/react";
import WaiverStep from "@/components/WaiverStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ap}`;
};

const tzOffset = (tz, utcDate) => {
  const dtf = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  const p = Object.fromEntries(dtf.formatToParts(utcDate).map((x) => [x.type, x.value]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - utcDate.getTime();
};
const bizToUtc = (dateStr, hm, bizTz) => {
  const [Y, Mo, D] = dateStr.split("-").map(Number);
  const [h, m] = hm.split(":").map(Number);
  const naive = Date.UTC(Y, Mo - 1, D, h, m);
  let guess = new Date(naive - tzOffset(bizTz, new Date(naive)));
  return new Date(naive - tzOffset(bizTz, guess));
};

export const BookingDialog = ({ open, onOpenChange, phone, phoneRaw, booking }) => {
  const cfg = booking || {};
  const virtualEnabled = cfg.virtual_enabled !== false;
  const [sessionType, setSessionType] = useState(null);
  const [date, setDate] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [dayStatus, setDayStatus] = useState({});
  const [bizTz, setBizTz] = useState(null);
  const [slots, setSlots] = useState(null);
  const [closed, setClosed] = useState(false);
  const [closedReason, setClosedReason] = useState(null);
  const [maxOut, setMaxOut] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/site/packages`).then(({ data }) => setPacks(data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email.includes("@") || email.length < 5) { setCredits(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/site/credits`, { params: { email } }).then(({ data }) => setCredits(data)).catch(() => setCredits(null));
    }, 600);
    return () => clearTimeout(t);
  }, [form.email]);

  const buyPack = async (p) => {
    if (form.name.trim().length < 1 || !form.email.includes("@")) {
      toast.error("Enter your name and email above first — credits get tied to your email");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/site/packages/checkout`, {
        package_id: p.id, name: form.name, email: form.email, phone: form.phone,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't start checkout"); }
  };

  const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const showLocal = sessionType === "virtual" && bizTz && clientTz && clientTz !== bizTz;

  useEffect(() => {
    if (!date) return;
    setSlots(null); setTime(null);
    axios.get(`${API}/site/booking/slots`, { params: { date: fmtDate(date) } })
      .then(({ data }) => { setSlots(data.slots || []); setClosed(!!data.closed); setClosedReason(data.reason || null); setMaxOut(data.max_days_out || null); })
      .catch(() => { setSlots([]); setClosed(true); setClosedReason(null); });
  }, [date]);

  useEffect(() => {
    if (!open) return;
    const load = (y, m) => axios.get(`${API}/site/booking/month`, { params: { year: y, month: m } })
      .then(({ data }) => { if (data.timezone) setBizTz(data.timezone); return data.days || {}; }).catch(() => ({}));
    const y = month.getFullYear(), m = month.getMonth() + 1;
    const ny = m === 12 ? y + 1 : y, nm = m === 12 ? 1 : m + 1;
    Promise.all([load(y, m), load(ny, nm)]).then(([a, b]) => setDayStatus((prev) => ({ ...prev, ...a, ...b })));
  }, [open, month]);

  const pickDate = (d) => {
    setDate(d);
    if (d && (d.getMonth() !== month.getMonth() || d.getFullYear() !== month.getFullYear())) {
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  };

  const localLabel = (hm) => {
    if (!showLocal || !date) return fmt12(hm);
    try {
      const utc = bizToUtc(fmtDate(date), hm, bizTz);
      const label = utc.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      const shifted = utc.toLocaleDateString("en-CA") !== fmtDate(date);
      return shifted ? `${label} +1d` : label;
    } catch { return fmt12(hm); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time || form.name.trim().length < 1 || form.phone.trim().length < 7) return;
    setShowWaiver(true);
  };

  const doBook = async (waiverInfo) => {
    setBusy(true);
    try {
      await axios.post(`${API}/site/booking/request`, {
        date: fmtDate(date), time, ...form,
        session_type: sessionType || "in_person", client_tz: clientTz,
        waiver_signature_id: waiverInfo.waiver_signature_id,
      });
      setDone(true);
    } catch (err) {
      setShowWaiver(false);
      toast.error(err?.response?.data?.detail || "Couldn't book — please try another slot.");
    } finally { setBusy(false); }
  };

  const reset = (o) => { onOpenChange(o); if (!o) { setSessionType(null); setDate(null); setMonth(new Date()); setSlots(null); setTime(null); setDone(false); setShowWaiver(false); setForm({ name: "", phone: "", email: "", note: "" }); } };
  const anyAvailable = (slots || []).some((s) => s.available);
  const activeType = !virtualEnabled ? "in_person" : sessionType;

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-lg bg-[#121212] border-white/15 text-white max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-bold uppercase tracking-wide">Book a Private Session</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {activeType === "virtual" ? (cfg.virtual_blurb || "Live video training from anywhere.")
              : activeType === "in_person" ? (cfg.inperson_blurb || "$100 per session — not per person. Bring family or friends.")
              : "$100 per session — not per person. Train in person or live on Google Meet."}
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-6 text-center" data-testid="booking-success">
            <div className="text-2xl font-bold text-[#FFE01A]">Request sent!</div>
            <p className="text-[#A1A1AA] mt-2">
              We'll confirm your {sessionType === "virtual" ? "virtual session" : "session"} shortly by text{form.email ? " or email" : ""}.
              {sessionType === "virtual" && form.email ? " Your Google Meet link will arrive with the confirmation email." : ""}
            </p>
            <button onClick={() => reset(false)} className="mt-5 bg-[#FFE01A] text-black font-bold uppercase px-6 py-3 border-0 cursor-pointer" data-testid="booking-done-close">Done</button>
          </div>
        ) : showWaiver ? (
          <div className="py-1">
            <button onClick={() => setShowWaiver(false)} className="text-xs underline text-white/60 bg-transparent border-0 cursor-pointer mb-2" data-testid="waiver-back-button">&larr; Back to details</button>
            <WaiverStep name={form.name} email={form.email} phone={form.phone}
              source="booking" refInfo={`${fmtDate(date)} ${time} ${sessionType || "in_person"}`}
              busy={busy} submitLabel="Sign & request session" onSigned={doBook} />
          </div>
        ) : virtualEnabled && !sessionType ? (
          <div className="flex flex-col gap-3 py-2" data-testid="booking-type-step">
            <button onClick={() => setSessionType("in_person")} data-testid="booking-type-inperson"
              className="group text-left border border-white/20 hover:border-[#FFE01A] bg-black/40 px-5 py-4 cursor-pointer transition-colors">
              <div className="flex items-center gap-2.5 font-bold uppercase tracking-wide text-white">
                <MapPin size={19} weight="fill" className="text-[#FFE01A]" /> {cfg.inperson_title || "In Person"}
              </div>
              <div className="text-sm text-[#A1A1AA] mt-1.5">{cfg.inperson_blurb || "Train with us at our location."}</div>
            </button>
            <button onClick={() => setSessionType("virtual")} data-testid="booking-type-virtual"
              className="group text-left border border-white/20 hover:border-[#FFE01A] bg-black/40 px-5 py-4 cursor-pointer transition-colors">
              <div className="flex items-center gap-2.5 font-bold uppercase tracking-wide text-white">
                <VideoCamera size={19} weight="fill" className="text-[#FFE01A]" /> {cfg.virtual_title || "Virtual · Google Meet"}
              </div>
              <div className="text-sm text-[#A1A1AA] mt-1.5">{cfg.virtual_blurb || "Live video training from anywhere."}</div>
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {virtualEnabled && (
              <div className="flex items-center justify-center gap-2 text-xs" data-testid="booking-type-switch">
                <span className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wide text-[#FFE01A]">
                  {sessionType === "virtual" ? <VideoCamera size={14} weight="fill" /> : <MapPin size={14} weight="fill" />}
                  {sessionType === "virtual" ? (cfg.virtual_title || "Virtual · Google Meet") : (cfg.inperson_title || "In Person")}
                </span>
                <button onClick={() => { setSessionType(null); setDate(null); setTime(null); }}
                  className="text-[#71717A] underline bg-transparent border-0 cursor-pointer" data-testid="booking-type-change">change</button>
              </div>
            )}
            <div className="flex justify-center">
              <style>{`
                .e650-day-full:not([aria-selected="true"]):not(:disabled){color:rgba(255,255,255,.5)}
                .e650-day-open:not([aria-selected="true"]):not(:disabled){color:#fff}
                .e650-day-closed:not([aria-selected="true"]):not(:disabled){color:#71717A}
              `}</style>
              <Calendar mode="single" selected={date} onSelect={pickDate}
                month={month} onMonthChange={setMonth}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                modifiers={{
                  full: (d) => dayStatus[fmtDate(d)] === "full",
                  openday: (d) => dayStatus[fmtDate(d)] === "open",
                  closedday: (d) => dayStatus[fmtDate(d)] === "closed" || dayStatus[fmtDate(d)] === "away",
                }}
                modifiersClassNames={{ full: "e650-day-full", openday: "e650-day-open", closedday: "e650-day-closed" }}
                classNames={{
                  head_cell: "text-white/80 rounded-md w-8 font-normal text-[0.8rem]",
                  day_selected: "bg-white text-black hover:bg-white hover:text-black focus:bg-white focus:text-black",
                  day_outside: "day-outside text-[#71717A] aria-selected:bg-white aria-selected:text-black aria-selected:opacity-100",
                  day_today: "bg-transparent text-white outline outline-1 outline-white/90 outline-offset-[-3px] aria-selected:bg-white aria-selected:text-black aria-selected:outline-0",
                }}
                className="bg-[#0A0A0A] border border-white/10 rounded-none text-white" data-testid="booking-calendar" />
            </div>
            {date && slots === null && <div className="text-center text-[#71717A] text-sm">Checking availability…</div>}
            {date && slots !== null && (
              closed ? (
                <div className="text-center text-sm text-[#A1A1AA]" data-testid="booking-closed">
                  {closedReason === "away" ? "We're away this day — please pick another date."
                    : closedReason === "too_far" ? `Online booking is open up to ${maxOut || 60} days ahead — this date isn't open yet. Check back closer to the date, or call/text us.`
                    : "No sessions this day — try another date."}
                </div>
              ) : (
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#71717A] mb-2">Available times</div>
                  {showLocal && (
                    <div className="text-xs text-[#FFE01A] mb-2" data-testid="booking-tz-note">
                      Times shown in your local time ({clientTz.replace(/_/g, " ")})
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2" data-testid="booking-slots">
                    {slots.map((s) => (
                      <button key={s.time} disabled={!s.available} onClick={() => setTime(s.time)}
                        data-testid={`slot-${s.time}`}
                        className={`px-2 py-2.5 text-sm font-semibold border cursor-pointer ${time === s.time ? "bg-[#FFE01A] text-black border-[#FFE01A]" : s.available ? "bg-transparent text-white border-white/20 hover:border-[#FFE01A]" : "bg-white/5 text-[#3F3F46] border-white/5 cursor-not-allowed line-through"}`}>
                        {localLabel(s.time)}
                      </button>
                    ))}
                  </div>
                  {!anyAvailable && (
                    <div className="mt-3 text-sm text-[#A1A1AA] text-center" data-testid="booking-full-note">
                      Fully booked this day. Try a later date, or{" "}
                      <a href={`tel:${phoneRaw}`} className="text-[#FFE01A]">call/text {phone}</a>.
                    </div>
                  )}
                </div>
              )
            )}
            {time && (
              <form onSubmit={submit} className="flex flex-col gap-3 border-t border-white/10 pt-4">
                <input data-testid="booking-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="booking-phone" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                  <input data-testid="booking-email" type="email" required={sessionType === "virtual"}
                    placeholder={sessionType === "virtual" ? "Email (for your Meet link)" : "Email (optional)"}
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                </div>
                <textarea data-testid="booking-note" rows={2} placeholder="Anything we should know? (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm resize-y" />
                {credits && credits.balance > 0 && (
                  <div className="border border-[#FFE01A]/50 bg-[#FFE01A]/10 px-4 py-2.5 text-sm text-[#FFE01A]" data-testid="booking-credits-banner">
                    ✓ You have <b>{credits.balance} session credit{credits.balance > 1 ? "s" : ""}</b> — this booking uses 1, no payment needed.
                    {credits.expires_at ? ` (valid until ${credits.expires_at})` : ""}
                  </div>
                )}
                {packs.length > 0 && (!credits || !credits.balance) && (
                  <div className="border border-white/10 bg-white/5 px-4 py-3" data-testid="booking-packs-upsell">
                    <div className="text-xs uppercase tracking-[0.15em] text-[#71717A] mb-2">Save with a session pack</div>
                    <div className="flex flex-wrap gap-2">
                      {packs.map((p) => (
                        <button key={p.id} type="button" onClick={() => buyPack(p)} data-testid={`buy-pack-${p.id}`}
                          className="border border-[#FFE01A]/50 hover:bg-[#FFE01A]/15 bg-transparent text-white text-xs font-bold px-3 py-2 cursor-pointer">
                          {p.name} · {p.sessions} sessions · ${(p.price_cents / 100).toFixed(0)}
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] text-[#71717A] mt-1.5">Buy once, then book with your email — credits apply automatically.</div>
                  </div>
                )}
                <button type="submit" disabled={busy} data-testid="booking-submit"
                  className="bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer border-0 disabled:opacity-50">
                  {busy ? "Sending…" : `Continue — sign waiver`}
                </button>
              </form>
            )}
            <div className="text-center text-xs text-[#71717A] flex items-center justify-center gap-1.5">
              <Phone size={13} /> Prefer to talk? Call or text <a href={`tel:${phoneRaw}`} className="text-[#FFE01A]">{phone}</a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
